import React from 'react';
import { View, StyleSheet, ScrollView } from 'react-native';
import {
  Text,
  Card,
  Button,
  Chip,
  useTheme,
  Divider,
} from 'react-native-paper';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import type { AlternativeSuggestionsProps } from './JourneyPreviewTypes';

export function AlternativeSuggestions({
  alternatives,
  currentJourney,
  onSelectAlternative,
  style,
}: AlternativeSuggestionsProps) {
  const theme = useTheme();

  if (alternatives.length === 0) {
    return null;
  }

  const formatDuration = (weeks: number) => {
    if (weeks === 1) return '1 week';
    if (weeks < 4) return `${weeks} weeks`;
    const months = Math.round(weeks / 4);
    return months === 1 ? '1 month' : `${months} months`;
  };

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return theme.colors.primary;
    if (confidence >= 0.6) return theme.colors.secondary;
    if (confidence >= 0.4) return '#FFC107';
    return theme.colors.error;
  };

  const compareWithCurrent = (alternative: any) => {
    const comparisons = [];

    if (alternative.duration_weeks !== currentJourney.duration_weeks) {
      const diff = alternative.duration_weeks - currentJourney.duration_weeks;
      comparisons.push(
        diff > 0
          ? `${Math.abs(diff)} weeks longer`
          : `${Math.abs(diff)} weeks shorter`
      );
    }

    const uniqueFocusAreas = alternative.focus_areas.filter(
      (area: string) => !currentJourney.focus_areas.includes(area)
    );

    if (uniqueFocusAreas.length > 0) {
      comparisons.push(`Focus on ${uniqueFocusAreas.slice(0, 2).join(', ')}`);
    }

    return comparisons;
  };

  return (
    <View style={[styles.container, style]}>
      <Text variant='titleMedium' style={styles.title}>
        Alternative Journeys
      </Text>
      <Text variant='bodySmall' style={styles.subtitle}>
        Other AI-generated options based on your assessment
      </Text>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        style={styles.scrollView}
      >
        {alternatives.map((alternative, index) => {
          const comparisons = compareWithCurrent(alternative);

          return (
            <Card
              key={alternative.id}
              style={styles.alternativeCard}
              mode='outlined'
            >
              <Card.Content>
                {/* Header */}
                <View style={styles.cardHeader}>
                  <Text variant='titleSmall' style={styles.alternativeTitle}>
                    {alternative.title}
                  </Text>
                  <Chip
                    mode='outlined'
                    compact
                    style={[
                      styles.confidenceChip,
                      {
                        borderColor: getConfidenceColor(alternative.confidence),
                      },
                    ]}
                    textStyle={[
                      styles.confidenceChipText,
                      { color: getConfidenceColor(alternative.confidence) },
                    ]}
                  >
                    {Math.round(alternative.confidence * 100)}%
                  </Chip>
                </View>

                {/* Description */}
                <Text variant='bodySmall' style={styles.alternativeDescription}>
                  {alternative.description}
                </Text>

                {/* Duration */}
                <View style={styles.durationContainer}>
                  <Text variant='labelSmall' style={styles.durationLabel}>
                    Duration
                  </Text>
                  <Text variant='bodyMedium' style={styles.durationValue}>
                    {formatDuration(alternative.duration_weeks)}
                  </Text>
                </View>

                {/* Focus Areas */}
                <View style={styles.focusAreasContainer}>
                  <Text variant='labelSmall' style={styles.focusAreasLabel}>
                    Focus Areas
                  </Text>
                  <View style={styles.focusAreasChips}>
                    {alternative.focus_areas
                      .slice(0, 3)
                      .map((area: string, areaIndex: number) => (
                        <Chip
                          key={areaIndex}
                          mode='outlined'
                          compact
                          style={styles.focusAreaChip}
                          textStyle={styles.focusAreaChipText}
                        >
                          {area}
                        </Chip>
                      ))}
                    {alternative.focus_areas.length > 3 && (
                      <Text variant='bodySmall' style={styles.moreFocusAreas}>
                        +{alternative.focus_areas.length - 3}
                      </Text>
                    )}
                  </View>
                </View>

                {/* Comparisons */}
                {comparisons.length > 0 && (
                  <View style={styles.comparisonsContainer}>
                    <Text variant='labelSmall' style={styles.comparisonsLabel}>
                      Compared to current
                    </Text>
                    {comparisons.map((comparison, compIndex) => (
                      <Text
                        key={compIndex}
                        variant='bodySmall'
                        style={styles.comparisonItem}
                      >
                        • {comparison}
                      </Text>
                    ))}
                  </View>
                )}

                {/* AI Reasoning */}
                <View style={styles.reasoningContainer}>
                  <Text variant='labelSmall' style={styles.reasoningLabel}>
                    Why this alternative?
                  </Text>
                  <Text variant='bodySmall' style={styles.reasoningText}>
                    {alternative.reasoning}
                  </Text>
                </View>

                {/* Tradeoffs */}
                {alternative.tradeoffs.length > 0 && (
                  <View style={styles.tradeoffsContainer}>
                    <Text variant='labelSmall' style={styles.tradeoffsLabel}>
                      Tradeoffs
                    </Text>
                    {alternative.tradeoffs
                      .slice(0, 2)
                      .map((tradeoff: string, tradeoffIndex: number) => (
                        <Text
                          key={tradeoffIndex}
                          variant='bodySmall'
                          style={styles.tradeoffItem}
                        >
                          ⚖️ {tradeoff}
                        </Text>
                      ))}
                  </View>
                )}

                {/* Confidence Indicator */}
                <View style={styles.confidenceContainer}>
                  <ConfidenceIndicator
                    confidence={alternative.confidence}
                    showLabel={false}
                    size='small'
                  />
                </View>
              </Card.Content>

              <Card.Actions style={styles.cardActions}>
                <Button
                  mode='outlined'
                  onPress={() => onSelectAlternative(alternative)}
                  style={styles.selectButton}
                  compact
                >
                  Select This
                </Button>
              </Card.Actions>
            </Card>
          );
        })}
      </ScrollView>

      {/* Footer */}
      <View style={styles.footer}>
        <Text variant='bodySmall' style={styles.footerText}>
          💡 These alternatives are generated based on different interpretations
          of your assessment
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    marginVertical: 16,
  },
  title: {
    fontWeight: '600',
    marginBottom: 4,
  },
  subtitle: {
    opacity: 0.7,
    marginBottom: 16,
  },
  scrollView: {
    marginBottom: 12,
  },
  scrollContent: {
    paddingHorizontal: 4,
    gap: 12,
  },
  alternativeCard: {
    width: 280,
    marginRight: 12,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  alternativeTitle: {
    fontWeight: '600',
    flex: 1,
    marginRight: 8,
  },
  confidenceChip: {
    height: 20,
  },
  confidenceChipText: {
    fontSize: 9,
    fontWeight: '500',
  },
  alternativeDescription: {
    lineHeight: 16,
    marginBottom: 12,
    opacity: 0.8,
  },
  durationContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  durationLabel: {
    opacity: 0.7,
  },
  durationValue: {
    fontWeight: '500',
  },
  focusAreasContainer: {
    marginBottom: 12,
  },
  focusAreasLabel: {
    opacity: 0.7,
    marginBottom: 4,
  },
  focusAreasChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    alignItems: 'center',
  },
  focusAreaChip: {
    height: 20,
  },
  focusAreaChipText: {
    fontSize: 9,
  },
  moreFocusAreas: {
    opacity: 0.6,
    fontSize: 10,
  },
  comparisonsContainer: {
    marginBottom: 12,
    padding: 8,
    backgroundColor: 'rgba(0,0,0,0.05)',
    borderRadius: 6,
  },
  comparisonsLabel: {
    opacity: 0.7,
    marginBottom: 4,
    fontWeight: '500',
  },
  comparisonItem: {
    opacity: 0.8,
    fontSize: 11,
    lineHeight: 14,
  },
  reasoningContainer: {
    marginBottom: 12,
  },
  reasoningLabel: {
    opacity: 0.7,
    marginBottom: 4,
  },
  reasoningText: {
    lineHeight: 16,
    opacity: 0.8,
    fontStyle: 'italic',
  },
  tradeoffsContainer: {
    marginBottom: 12,
  },
  tradeoffsLabel: {
    opacity: 0.7,
    marginBottom: 4,
  },
  tradeoffItem: {
    opacity: 0.7,
    fontSize: 11,
    lineHeight: 14,
    marginBottom: 2,
  },
  confidenceContainer: {
    marginBottom: 8,
  },
  cardActions: {
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  selectButton: {
    minWidth: 100,
  },
  footer: {
    marginTop: 8,
    paddingHorizontal: 8,
  },
  footerText: {
    opacity: 0.6,
    fontStyle: 'italic',
    textAlign: 'center',
  },
});
